import * as React from "react";
import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import CheckIcon from "@mui/icons-material/Check";
import {
  useIssueFieldAdminStockMutation,
  useGetFieldAdminStockBalanceQuery,
  useGetFieldAdminStockOnVanQuery,
} from "./fieldAdminApi";
import { showSuccess } from "../../Global/notifications/notificationsSlice";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { FONT_FAMILY, MONO_FONT_FAMILY, installerLabel } from "./designTokens";
import { FormPage } from "./StockFormShell";
import { InstallerAvatar, useTeamRoster } from "./HS/InstallerRoster";
import { useConfirm, FACheckbox } from "./FAControls";
import { FASectionLabel, FANotesField, FAEmptyState } from "./FALayout";

export default function StockIssue() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { ask, confirmDialog } = useConfirm();
  const { installers, nameById } = useTeamRoster();

  const { data: balance } = useGetFieldAdminStockBalanceQuery();
  const [issueStock, { isLoading, error }] = useIssueFieldAdminStockMutation();

  const [installerId, setInstallerId] = useState(null);
  // A Set of serials rather than one string: a handover is usually several devices, and the
  // register keeps a row per device either way.
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [notes, setNotes] = useState("");

  const { data: vanData } = useGetFieldAdminStockOnVanQuery();
  const onVan = vanData?.devices || [];

  const query = search.trim().toLowerCase();
  const visible = query
    ? onVan.filter((d) => `${d.device_serial} ${d.device_imsi || ""}`.toLowerCase().includes(query))
    : onVan;

  const toggleDevice = (serial) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(serial)) next.delete(serial); else next.add(serial);
    return next;
  });
  const issuedToday = new Map((balance?.per_installer || []).map((row) => [row.installer_id, row.issued]));

  const handleSubmit = async () => {
    if (!installerId) {
      await ask({
        title: "No installer selected",
        body: "Pick the installer this device is going to before confirming the assignment.",
        confirmLabel: "OK",
        cancelLabel: "Dismiss",
      });
      return;
    }
    const serials = [...selected];
    const proceed = await ask({
      title: "Confirm assignment?",
      body: `Assigns ${serials.length} device${serials.length === 1 ? "" : "s"} to ${installerLabel(nameById.get(installerId), installerId)}.`,
      confirmLabel: "Confirm assignment",
    });
    if (!proceed) return;

    const result = await issueStock({
      installer_id: installerId,
      device_serials: serials,
      notes: notes || undefined,
    });
    if (result.error) return;
    dispatch(showSuccess(`${serials.length} device${serials.length === 1 ? "" : "s"} assigned`));
    navigate("/field-admin/stock/");
  };

  return (
    <FormPage
      eyebrow="Step 2 of stock workflow"
      title="Distribute to Installers"
      subtitle="Assign devices from the van to the installer who'll fit them today."
      onSubmit={handleSubmit}
      submitLabel={isLoading
        ? "Saving…"
        : `Confirm assignment${selected.size ? ` (${selected.size})` : ""}`}
      submitDisabled={isLoading || !installerId || selected.size === 0}
      // Reports the two things the button needs, in the order they are chosen, so a disabled
      // button always says which half is missing. The van figure is this list's own count —
      // balance.on_van is a day figure and would contradict the picker header above.
      summary={
        !installerId
          ? "Pick an installer to continue"
          : selected.size === 0
          ? `${installerLabel(nameById.get(installerId), installerId)} selected · pick devices from the van`
          : `${selected.size} device${selected.size === 1 ? "" : "s"} → ${installerLabel(nameById.get(installerId), installerId)} · ${onVan.length} on van`
      }
      error={error?.data?.error_message}
    >
      <FASectionLabel chip="Step 1 of 2">Pick installer</FASectionLabel>

      {/* The roster as selectable cards, replacing a raw numeric "Installer ID" field. */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" }, gap: "10px" }}>
        {installers.map((installer) => {
          const selected = installerId === installer.id;
          return (
            <Box
              key={installer.id}
              component="button"
              type="button"
              onClick={() => setInstallerId(selected ? null : installer.id)}
              // .installer-card — centred, per the design, so a row of technicians reads as
              // a set of faces rather than a list of left-aligned records.
              sx={{
                position: "relative", textAlign: "center", cursor: "pointer", fontFamily: FONT_FAMILY,
                display: "flex", flexDirection: "column", alignItems: "center", gap: "8px",
                backgroundColor: selected ? "rgba(253,185,64,0.06)" : CARD_BG,
                border: `1px solid ${selected ? AMBER : BORDER}`,
                borderRadius: "12px", px: "12px", py: "14px", minWidth: 0,
                transition: "border-color 0.2s",
                "&:hover": { borderColor: selected ? AMBER : "rgba(253,185,64,0.35)" },
              }}
            >
              <InstallerAvatar name={installer.name} id={installer.id} size={44} />
              <Box sx={{ minWidth: 0, width: "100%" }}>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#fff", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {installer.name}
                </Typography>
                <Typography sx={{ fontSize: "0.66rem", color: selected ? AMBER : "rgba(255,255,255,0.5)", fontVariantNumeric: "tabular-nums", mt: "2px" }}>
                  {issuedToday.get(installer.id) || 0} issued today
                </Typography>
              </Box>
              {/* .selected-mark — a filled badge, not a bare tick: at this size the tick alone
                  was easy to miss against the card's own border. */}
              {selected ? (
                <Box sx={{
                  position: "absolute", top: "8px", right: "8px",
                  width: "18px", height: "18px", borderRadius: "50%",
                  backgroundColor: AMBER, color: "#0D1117",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <CheckIcon sx={{ fontSize: "0.72rem" }} />
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>

      <FASectionLabel chip="Step 2 of 2">Pick devices from van</FASectionLabel>

      {/* .device-list — the van's live inventory, replacing a typed serial. The serial was
          free text because nothing served the on-van list; stock/on-van/ does now, so the
          field admin picks what is actually in the vehicle instead of transcribing it. */}
      <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px",
          p: "12px 16px", borderBottom: `1px solid ${BORDER}`, backgroundColor: "rgba(255,255,255,0.015)",
        }}>
          <Box
            component="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by serial or IMSI…"
            sx={{
              flex: 1, maxWidth: "280px", minWidth: 0, p: "8px 12px", boxSizing: "border-box",
              backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
              color: "#fff", borderRadius: "8px", fontSize: "0.84rem", fontFamily: MONO_FONT_FAMILY,
              outline: "none",
              "&:focus": { borderColor: "rgba(253,185,64,0.45)" },
              "&::placeholder": { color: "rgba(255,255,255,0.35)" },
            }}
          />
          {/* The van count is this list's own, not the balance page's on_van — that one is a
              day figure (today's collections minus today's issues) and would disagree here. */}
          <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.55)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
            <Box component="span" sx={{ color: AMBER, fontWeight: 700 }}>{selected.size}</Box>
            {` selected · ${onVan.length} on van`}
          </Typography>
        </Box>

        {onVan.length === 0 ? (
          <FAEmptyState py="46px">
            Nothing on the van to distribute. Collect stock first — step 1 of the workflow.
          </FAEmptyState>
        ) : visible.length === 0 ? (
          <FAEmptyState py="46px">No device matches “{search.trim()}”.</FAEmptyState>
        ) : (
          <Box sx={{ maxHeight: "340px", overflowY: "auto" }}>
            {visible.map((device, index) => {
              const isSelected = selected.has(device.device_serial);
              return (
                <Box
                  key={device.device_serial}
                  component="label"
                  sx={{
                    display: "flex", alignItems: "center", gap: "12px", p: "12px 16px", cursor: "pointer",
                    borderBottom: index === visible.length - 1 ? "none" : "1px solid rgba(255,255,255,0.04)",
                    backgroundColor: isSelected ? "rgba(253,185,64,0.04)" : "transparent",
                    transition: "background 0.15s",
                    "&:hover": { backgroundColor: isSelected ? "rgba(253,185,64,0.06)" : "rgba(255,255,255,0.02)" },
                  }}
                >
                  <FACheckbox compact checked={isSelected} onChange={() => toggleDevice(device.device_serial)} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: MONO_FONT_FAMILY, fontSize: "0.88rem", color: "#fff", fontWeight: 500 }}>
                      {device.device_serial}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mt: "1px" }}>
                      {[device.device_imsi ? `IMSI · ${device.device_imsi}` : null, device.device_model]
                        .filter(Boolean).join(" · ") || "No IMSI or model captured"}
                    </Typography>
                  </Box>
                  {/* One product line for now, so the tag is constant rather than guessed
                      from the free-text model captured at collection. */}
                  <Box component="span" sx={{
                    fontSize: "0.66rem", fontWeight: 600, px: "8px", py: "3px", borderRadius: "4px",
                    textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
                    backgroundColor: "rgba(34,197,94,0.14)", color: "#4ADE80",
                  }}>
                    Tjommie
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* .summary-strip — what is about to happen, in one line, before confirming. */}
      {selected.size > 0 && installerId ? (
        <Box sx={{
          mt: "18px", p: "14px 18px", borderRadius: "12px",
          background: "linear-gradient(135deg, rgba(253,185,64,0.12), rgba(253,185,64,0.04))",
          border: "1px solid rgba(253,185,64,0.28)",
          display: "flex", alignItems: "center", gap: "12px",
        }}>
          <Box sx={{
            width: "36px", height: "36px", borderRadius: "9px", flexShrink: 0,
            backgroundColor: "rgba(253,185,64,0.18)", color: AMBER,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <CheckIcon sx={{ fontSize: "1.1rem" }} />
          </Box>
          <Typography sx={{ fontSize: "0.92rem", color: "#fff", lineHeight: 1.4 }}>
            <Box component="span" sx={{ color: AMBER, fontWeight: 700 }}>{selected.size}</Box>
            {` device${selected.size === 1 ? "" : "s"} → `}
            <Box component="span" sx={{ color: AMBER, fontWeight: 700 }}>
              {installerLabel(nameById.get(installerId), installerId)}
            </Box>
          </Typography>
        </Box>
      ) : null}

      <Box>
        <Typography sx={{ fontSize: "0.74rem", fontWeight: 600, color: "rgba(255,255,255,0.55)", mb: "6px" }}>Assignment note (optional)</Typography>
        <FANotesField
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Open-format note attached to this installer's assignment record."
          minHeight="70px"
        />
      </Box>

      {confirmDialog}
    </FormPage>
  );
}
